import { useController, useFormContext } from 'react-hook-form';
import { AutoComplete } from '../../../components/AutoComplete/AutoComplete.js';
import { sortByActive } from '../../../components/AutoComplete/utils.js';
import {
  eInvoicingOperatorMsg,
  eInvoicingSourceMsg,
  fieldCantBeEmptyMsg,
  noSelectionMsg,
  selectMsg,
  t,
} from '../../i18n/index.js';
import { getElectronicInvoiceOperatorDropDownOptions } from '../../utils/billingAccountUtils.js';
import { getTextError } from '../utils.js';
import { TextInput } from './TextInput';
import type { BillChannel } from '../../../generated/api/billChannel.js';
import type { EOperatorItem } from '../../utils/billingAccountUtils.js';

interface ElectronicOperatorProps {
  billChannels: BillChannel[];
  name: string;
  onOperatorChange?: (selectedOperator?: EOperatorItem) => void;
  disabled?: boolean;
  readonly?: boolean;
}

export const ElectronicOperator = (props: ElectronicOperatorProps) => {
  const rawOperators = getElectronicInvoiceOperatorDropDownOptions(props.billChannels);
  const electronicOperators = sortByActive(rawOperators, item => item.label);
  const name = props.name;
  const methods = useFormContext();
  const { control, setValue, setError, watch } = methods;
  const rules = { required: { value: true, message: t.VPVR(fieldCantBeEmptyMsg) } };
  const { field, fieldState, formState } = useController({ name, control, rules });
  const currentValue = watch(name);
  const selectedOption = electronicOperators.find(i => i.value === currentValue);

  const error = getTextError(formState.isSubmitted, fieldState.isTouched, fieldState.error);

  if (props.readonly) {
    return <TextInput name={name} label={t.WVLB(eInvoicingOperatorMsg)} readonly={true} />;
  }

  return (
    <div className="large-min-height">
      <AutoComplete<EOperatorItem>
        inputRef={field.ref}
        options={electronicOperators}
        defaultOption={selectedOption}
        getUniqueId={(i: EOperatorItem) => i.value}
        getDisplayValue={(i: EOperatorItem) => i.label}
        getDisplayHtml={(i: EOperatorItem) => i.html}
        onInputBlur={field.onBlur}
        onInputChange={(e, item) => {
          setValue(name, item.value);
          setError(name, { message: undefined });

          if (props.onOperatorChange && item.value) {
            const selectedOperator = electronicOperators.find(op => op.value === item.value);
            props.onOperatorChange(selectedOperator);
          }
        }}
        id={`${name}-autocomplete`}
        noOptionsMsg={t.ZW5W(noSelectionMsg)}
        label={t.WVLB(eInvoicingOperatorMsg)}
        config={{ isClearable: false, isSortable: false }}
        placeholder={t.QRYV(selectMsg)}
        error={error}
        disabled={props.disabled}
        key={`eOperator-${currentValue}`} // Force re-render when value changes
      />
      <div className="ds-font-size--small ds-inputhelp ds-margin-bottom--4">{t.AO0M(eInvoicingSourceMsg)}</div>
    </div>
  );
};
